import type {
    AbstractAnalyticsDispatcher,
    OverridableActionsTypes,
    OverridableBusinessEventTypes,
} from "@asurion-core/eventualize";
import type { Logger } from "@expert/logging";

import { removeMdnFormatting } from "@expert/common-ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { DefaultPayload } from "../api";
import type { VerizonReplacementDevice, VerizonReplacementOption } from "../api/prepareVerizonReplacement";
import type { ActivationFlowType } from "../api/verifyCustomer";
import type { VerifyVerizonCustomerResult } from "../api/verifyVerizonCustomer";
import type { GuideActionOption, GuideOption } from "../logic-builder";
import type { SessionMetadata } from "../store";
import type { GuideSessionContext } from "../types";
import type {
    ActivationAction,
    ActivationActionInput,
    ActivationActionResult,
    DeviceActivationCustomerState,
} from "./types";

import { activateDevice, verifyCustomer } from "../api";
import { authorizeVerizonInAcss } from "../api/authorizeVerizonInAcss";
import { generatePasscode } from "../api/generatePasscode";
import { generatePasscodeForVerification } from "../api/generatePasscodeForVerification";
import { prepareVerizonReplacement } from "../api/prepareVerizonReplacement";
import { hasValidImeiCheckDigit, submitTypedVerizonDevice } from "../api/submitTypedVerizonDevice";
import { verifyPasscode } from "../api/verifyPasscode";
import { verifyPasscodeForVerification } from "../api/verifyPasscodeForVerification";
import { verifyVerizonCustomer } from "../api/verifyVerizonCustomer";
import { encryptVerizonPin, preloadVerizonPie, VerizonPieUnavailableError } from "../api/verizonPinEncryption";
import {
    type BlockedActivation,
    buildActivationInputSignature,
    buildVerizonActivationInputSignature,
    isRetryBlocked,
    nextBlockedActivation,
} from "../components/activationRetryBlock";
import { resolveCustomerVerification } from "../components/customerVerificationResult";
import {
    EMPTY_DEVICE_ACTIVATION_CUSTOMER_STATE,
    selectDeviceActivationCustomerState,
    selectExtraAuthorizedToken,
    selectGuideSession,
    selectVerifiedCustomerToken,
    selectVerizonReplacementOptions,
    selectVerizonReplacementToken,
    useGuideStore,
} from "../store";
import { routeVerizonPoa } from "./verizonPoaRoute";

type Dispatcher = AbstractAnalyticsDispatcher<OverridableBusinessEventTypes, OverridableActionsTypes>;

/**
 * Customer-state fields a guide step may fill from its text input via the `setCustomerState` action
 * (the option's `value` names the field). Anything else is ignored, so an authored step can never
 * write, and so persist, a PIN or passcode.
 */
const INPUT_CUSTOMER_FIELDS = ["iccid", "imei", "phoneNumber"] as const;
type InputCustomerField = (typeof INPUT_CUSTOMER_FIELDS)[number];
const isInputCustomerField = (field: string): field is InputCustomerField =>
    (INPUT_CUSTOMER_FIELDS as readonly string[]).includes(field);

const VALID_MDN = /^\d{10}$/;
const VALID_IMEI = /^\d{15}$/;

/** Actions only an expert with `isVerizonTypedImeiActivationEnabled` sees; their buttons are hidden otherwise. */
const TYPED_IMEI_ACTIONS: ReadonlySet<string> = new Set<ActivationAction>(["typeVerizonImei"]);

/** Dropdown actions whose options come from verification results rather than the authored flow. */
const DYNAMIC_DROPDOWN_ACTIONS: ReadonlySet<string> = new Set<ActivationAction>([
    "generatePasscode",
    "selectClaim",
    "selectVerizonReplacement",
]);

/**
 * How a Verizon PIN verification ended, as reported on `VerifyVerizonCustomer`: the backend's outcome, or
 * `ENCRYPTION_UNAVAILABLE` when the PIN could not be encrypted, so nothing was sent.
 */
type VerizonPinOutcome = VerifyVerizonCustomerResult["outcome"] | "ENCRYPTION_UNAVAILABLE";

/**
 * The `verifyVerizonPin` step's branches. Only a wrong PIN is retried; a locked PIN goes to ACSS, and every
 * failure goes to `failure`, the manual flow, as `prepareVerizonReplacement`'s does.
 */
const VERIZON_PIN_NEXT_STEP: Record<VerizonPinOutcome, string> = {
    VERIFIED: "success",
    INCORRECT_PIN: "incorrectPin",
    MAX_ATTEMPTS: "maxAttempts",
    REQUEST_FAILED: "failure",
    ENCRYPTION_UNAVAILABLE: "failure",
};

export const MISSING_MDN_NOTICE =
    "We lost the customer's phone number. Go back to the phone number step and re-enter it, then try again.";
export const INVALID_MDN_NOTICE =
    "The customer's phone number isn't a valid 10-digit number. Go back to the phone number step and correct it, then try again.";
export const MISSING_VERIFICATION_NOTICE =
    "We lost the customer's verification. Go back and verify the customer again, then try again.";
export const MISSING_VERIZON_REPLACEMENT_NOTICE =
    "We lost the replacement devices we found. Go back and look up the replacement device again, then try again.";

const CLAIM_DATE_FORMAT: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };

/**
 * A Verizon device as a dropdown option. Every device listed is on the verified line, so the line isn't shown;
 * the claim date tells apart two claims for the same model. The value is the device's place in the list, never
 * its token: the selected option's value is logged and sent to analytics.
 */
const toVerizonReplacementOption = (device: VerizonReplacementDevice, index: number): GuideOption => {
    const claimDate = new Date(device.claimDate);
    const claimed = Number.isNaN(claimDate.getTime())
        ? ""
        : ` · claim ${claimDate.toLocaleDateString("en-US", CLAIM_DATE_FORMAT)}`;
    const simType = device.eSim ? "eSIM" : "physical SIM";
    return { label: `${device.make} ${device.model}`.trim() + ` · ${simType}${claimed}`, value: String(index) };
};

export interface UseDeviceActivationArgs {
    actions: GuideActionOption[];
    currentStep: string;
    dispatcher: Dispatcher;
    extraData: Record<string, unknown>;
    logger: Logger;
    session: GuideSessionContext;
    sessionMetadata: SessionMetadata;
}

export interface UseDeviceActivation {
    assistContext: { activationFlowType?: ActivationFlowType; make?: string; model?: string };
    blockedReason?: string;
    deviceLabel: string;
    dropdownOptions: (authored: GuideOption[], dropdownAction?: string) => GuideOption[];
    failureReason?: string;
    handleAction: (action: ActivationAction, input: ActivationActionInput) => Promise<ActivationActionResult>;
    /** Whether to show an authored option. False for an action this expert isn't enabled for. */
    isOptionAvailable: (option: GuideOption) => boolean;
    isActivationBlocked: boolean;
    isActivationSubmitStep: boolean;
    isVerizon: boolean;
    /** Set when an action refused to call the backend over missing guide state; cleared on step change. */
    recoveryNotice?: string;
    reset: () => void;
    shouldUseAlternateText: boolean;
    /** Verizon only: the replacement device to confirm with the customer before activating. */
    verizonReplacement?: VerizonReplacementDevice;
}

export function useDeviceActivation({
    actions,
    currentStep,
    dispatcher,
    extraData,
    logger,
    session,
    sessionMetadata,
}: UseDeviceActivationArgs): UseDeviceActivation {
    const {
        apiBaseUrl,
        callSid,
        clientChannelId,
        isProdMode = false,
        isVerizonDeviceActivationApiEnabled,
        isVerizonTypedImeiActivationEnabled,
        partner,
        sessionId,
        ssoAccessToken,
        taskMdn,
    } = session;
    const isVerizon = partner === "verizon";
    const isVerizonApiEnabled = isVerizon && !!isVerizonDeviceActivationApiEnabled;
    const isVerizonTypedImeiEnabled = isVerizonApiEnabled && !!isVerizonTypedImeiActivationEnabled;

    // PIE is Verizon's script, so it is fetched only once a Verizon expert with the API enabled reaches the
    // PIN step, never on page load.
    const isVerizonPinStep =
        isVerizonApiEnabled && actions.some((option) => option.customAction === "verifyVerizonPin");
    useEffect(() => {
        if (isVerizonPinStep) preloadVerizonPie(isProdMode);
    }, [isVerizonPinStep, isProdMode]);

    // Customer state and verification tokens live in the session-keyed guide store, not component state:
    // the card unmounts whenever the guide closes (e.g. a Workspace V2 tab change) or the latest Simplr
    // message isn't a text step, while the Simplr room resumes at the step the expert left. Scoped to the
    // current room so an error-state restart (new room, first step) starts clean.
    const errorStateSessionId = useGuideStore((state) => selectGuideSession(state, sessionId).errorStateSessionId);
    const roomKey = errorStateSessionId || sessionId;
    const customerState = useGuideStore((state) => selectDeviceActivationCustomerState(state, sessionId, roomKey));
    const verifiedCustomerToken = useGuideStore((state) => selectVerifiedCustomerToken(state, sessionId, roomKey));
    const extraAuthorizedToken = useGuideStore((state) => selectExtraAuthorizedToken(state, sessionId, roomKey));
    const verizonReplacementToken = useGuideStore((state) => selectVerizonReplacementToken(state, sessionId, roomKey));
    const verizonReplacementOptions = useGuideStore((state) =>
        selectVerizonReplacementOptions(state, sessionId, roomKey),
    );
    // Whether this card mounted onto state an earlier mount stored (tab switch, remount, page reload).
    // Logged by the missing-MDN guard to tell a rehydration gap from a card that never had the MDN.
    // Tokens-only slices keep the empty sentinel as their customer state, so identity means "no answers".
    const hasStoredCustomerState = customerState !== EMPTY_DEVICE_ACTIVATION_CUSTOMER_STATE;
    const [mountedWithStoredState] = useState(hasStoredCustomerState);
    const [recoveryNotice, setRecoveryNotice] = useState<string | undefined>();

    // Writes re-check the room at write time: an action captures `roomKey` when it starts, and if the room
    // changed while its request was in flight (error-state restart), its result belongs to the old room.
    // It is dropped rather than written under the new key, whose guide starts over from the first step.
    const isCurrentRoom = () => {
        const currentErrorStateSessionId = selectGuideSession(useGuideStore.getState(), sessionId).errorStateSessionId;
        const currentRoomKey = currentErrorStateSessionId || sessionId;
        if (currentRoomKey === roomKey) return true;
        logger.warn(
            { sessionId, roomKey, currentRoomKey },
            "Solve Guide | Device activation | Room changed mid-request, dropping stale result",
        );
        return false;
    };
    const setCustomerState = (patch: Partial<DeviceActivationCustomerState>) => {
        if (isCurrentRoom()) useGuideStore.getState().setDeviceActivationCustomerState(sessionId, roomKey, patch);
    };
    const setVerifiedCustomerToken = (token: string) => {
        if (isCurrentRoom()) {
            useGuideStore.getState().setDeviceActivationTokens(sessionId, roomKey, { verifiedCustomerToken: token });
        }
    };
    const setExtraAuthorizedToken = (token: string) => {
        if (isCurrentRoom()) {
            useGuideStore.getState().setDeviceActivationTokens(sessionId, roomKey, { extraAuthorizedToken: token });
        }
    };
    /** The device activation will use, and with `options`, the devices the expert may pick from instead. */
    const setVerizonReplacement = (selected?: VerizonReplacementOption, options?: VerizonReplacementOption[]) => {
        if (!isCurrentRoom()) return;
        const { setDeviceActivationCustomerState, setDeviceActivationTokens } = useGuideStore.getState();
        if (selected) {
            const { token, ...device } = selected;
            setDeviceActivationCustomerState(sessionId, roomKey, {
                activationFlowType: "REPLACEMENT_DEVICE",
                verizonReplacement: device,
            });
            setDeviceActivationTokens(sessionId, roomKey, {
                verizonReplacementToken: token,
                ...(options && { verizonReplacementOptions: options }),
            });
        } else {
            setDeviceActivationCustomerState(sessionId, roomKey, { verizonReplacement: undefined });
            setDeviceActivationTokens(sessionId, roomKey, {
                verizonReplacementToken: "",
                verizonReplacementOptions: undefined,
            });
        }
    };
    // A failed activation stashes its reason in this ref (not state) so it does NOT render on the
    // activate screen where the button was clicked. It's promoted to the visible state only once we
    // navigate to the next step (the failure screen), and cleared on any later step change — handled
    // during render below.
    const pendingFailureReason = useRef<string | undefined>(undefined);
    const [failureReason, setFailureReason] = useState<string | undefined>();
    const [prevStep, setPrevStep] = useState(currentStep);
    const [blockedActivation, setBlockedActivation] = useState<BlockedActivation | undefined>();
    // The session this instance's retry block belongs to — lets us drop the block if the same GuideCard
    // instance is reused for a different session (multi-session chat without a per-chat remount key).
    const [blockSessionId, setBlockSessionId] = useState(sessionId);

    // On every step change, promote a pending failure reason (set by a failed activation on the
    // previous step) onto this step, otherwise clear it. Done during render so the reason only ever
    // appears on the failure step we navigate to — never on the activate screen or any later screen.
    if (currentStep !== prevStep) {
        setPrevStep(currentStep);
        const nextReason = pendingFailureReason.current;
        pendingFailureReason.current = undefined;
        if (nextReason !== failureReason) setFailureReason(nextReason);
        if (recoveryNotice) setRecoveryNotice(undefined);
    }

    // If this instance is reused for a different session (a chat layout without a per-chat remount key),
    // drop the retry block so one customer's block can't disable Activate for the next. Customer state and
    // tokens need no reset here: they are read from the store under the new session's key.
    if (sessionId !== blockSessionId) {
        setBlockSessionId(sessionId);
        setBlockedActivation(undefined);
        setRecoveryNotice(undefined);
    }

    // Verizon activates with the token minted for the looked-up device; its PIN token only unlocks the lookup.
    const activationToken = isVerizon ? verizonReplacementToken : extraAuthorizedToken || verifiedCustomerToken;

    // Fingerprint of the exact inputs sent to `activateDevice`. Used to decide whether a prior rejection
    // still applies: if the current inputs match the rejected ones, retrying would hit the same AT&T
    // business rule, so the Activate button stays disabled until an answer actually changes. Verizon keys
    // on the looked-up device instead: its token is re-minted by every lookup, even for the same device.
    const activationInputSignature = useMemo(
        () =>
            isVerizon
                ? buildVerizonActivationInputSignature({
                      activationFlowType: customerState.activationFlowType,
                      device: customerState.verizonReplacement,
                  })
                : buildActivationInputSignature({
                      activationFlowType: customerState.activationFlowType,
                      claimId: customerState.selectedClaimId,
                      imei: customerState.imei,
                      iccid: customerState.iccid,
                      token: activationToken,
                  }),
        [customerState, activationToken, isVerizon],
    );

    const defaultPayload: DefaultPayload = { callSid: callSid ?? "", clientChannelId, partner, sessionId };

    // Strips formatting and a +1 / leading 1 country code, so "1 (555) 123-4567" becomes "5551234567".
    const normalizeMdn = useCallback((mdn: string) => {
        const digits = removeMdnFormatting(mdn.replace("+1", ""));
        return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    }, []);
    const customerMdn = () => normalizeMdn(customerState.phoneNumber);

    /**
     * Guard for every request that sends the customer MDN. An empty or malformed MDN makes Layer7 reject
     * the call ("Missing mandatory parameters") and the backend surface it as a 500, so don't send it:
     * keep the expert on this step with a notice to re-enter the number. Logs lengths only, never the MDN.
     */
    const hasValidCustomerMdn = (action: ActivationAction) => {
        const mdn = customerMdn();
        if (VALID_MDN.test(mdn)) return true;
        logger.warn(
            {
                ...extraData,
                action,
                sessionId,
                roomKey,
                mdnLength: mdn.length,
                mountedWithStoredState,
                hasStoredCustomerState,
            },
            "Solve Guide | Device activation | Missing or invalid customer MDN, skipping request",
        );
        setRecoveryNotice(mdn ? INVALID_MDN_NOTICE : MISSING_MDN_NOTICE);
        return false;
    };

    /**
     * Guard for every request that needs a customer verification token. Tokens are in memory only, and
     * without one the backend can only reject the request, so keep the expert on this step instead.
     */
    const hasVerificationToken = (action: ActivationAction, token: string) => {
        if (token) return true;
        logger.warn(
            { ...extraData, action, sessionId, roomKey, mountedWithStoredState, hasStoredCustomerState },
            "Solve Guide | Device activation | Missing verification token, skipping request",
        );
        setRecoveryNotice(MISSING_VERIFICATION_NOTICE);
        return false;
    };

    const track = <TResult>(
        event: string,
        result: TResult,
        wasSuccessful: boolean,
        props?: Record<string, unknown>,
        skipOpenSearch = false,
    ) => {
        const target = skipOpenSearch ? dispatcher.withMeta({ SkipOpenSearch: true }) : dispatcher;
        void target.dispatchBusinessEvent(event, { wasSuccessful, ...extraData, ...props });
        return result;
    };

    const verifyPin = async (inputValue: string) => {
        const payload = { ...defaultPayload, mdn: customerMdn(), pin: inputValue };
        const result = await verifyCustomer(ssoAccessToken, payload, logger, apiBaseUrl);
        return track("VerifyCustomer", result, !!result.token);
    };

    const sendVerificationPasscode = async (targetMdn: string) => {
        const payload = { ...defaultPayload, mdn: customerMdn(), targetMdn: normalizeMdn(targetMdn) };
        const result = await generatePasscodeForVerification(ssoAccessToken, payload, logger, apiBaseUrl);
        return track("GeneratePasscodeForVerification", result, result.success);
    };

    const verifyVerificationPasscode = async (passcode: string) => {
        const payload = {
            ...defaultPayload,
            mdn: customerMdn(),
            passcode,
            transactionId: customerState.transactionId,
        };
        const result = await verifyPasscodeForVerification(ssoAccessToken, payload, logger, apiBaseUrl);
        return track("VerifyPasscodeForVerification", result, !!result.token);
    };

    const sendPasscode = async (selectedMdn: string) => {
        const mdn = normalizeMdn(selectedMdn);
        const payload = { ...defaultPayload, mdn };
        const result = await generatePasscode(ssoAccessToken, payload, verifiedCustomerToken, logger, apiBaseUrl);
        return track("GeneratePasscode", result, result.success, { mdn }, /* contains PII */ true);
    };

    const confirmPasscode = async (passcode: string) => {
        const payload = { ...defaultPayload, passcode, transactionId: customerState.transactionId };
        const result = await verifyPasscode(ssoAccessToken, payload, verifiedCustomerToken, logger, apiBaseUrl);
        return track("VerifyPasscode", result, !!result.token);
    };

    /**
     * Encrypts the PIN with Verizon's PIE, then verifies it. The plain PIN goes no further than
     * `encryptVerizonPin`: the request carries only the ciphertext, and neither logs nor analytics see it.
     */
    const checkVerizonPin = async (pin: string): Promise<VerifyVerizonCustomerResult | undefined> => {
        let encrypted;
        try {
            encrypted = await encryptVerizonPin(pin, isProdMode);
        } catch (error) {
            logger.warn(
                {
                    ...extraData,
                    verizonPieFailure: error instanceof VerizonPieUnavailableError ? error.failure : "unknown",
                },
                "Solve Guide | Device activation | Could not encrypt the Verizon PIN, using the manual flow",
            );
            return undefined;
        }
        const payload = { ...defaultPayload, mdn: customerMdn(), ...encrypted };
        return verifyVerizonCustomer(ssoAccessToken, payload, logger, apiBaseUrl);
    };

    const verifyVerizonPin = async (pin: string) => {
        const result = await checkVerizonPin(pin);
        const outcome: VerizonPinOutcome = result?.outcome ?? "ENCRYPTION_UNAVAILABLE";
        const token = result?.outcome === "VERIFIED" ? result.token : "";
        const errorCode = result?.outcome === "REQUEST_FAILED" ? result.errorCode || undefined : undefined;
        // Outcome codes only: never the PIN, its ciphertext or the MDN.
        return track("VerifyVerizonCustomer", { outcome, token }, outcome === "VERIFIED", {
            partner,
            outcome,
            ...(errorCode && { errorCode }),
        });
    };

    const authorizeInAcss = async () => {
        const payload = { ...defaultPayload, mdn: customerMdn() };
        const result = await authorizeVerizonInAcss(ssoAccessToken, payload, logger, apiBaseUrl);
        const token = result.outcome === "AUTHORIZED" ? result.token : "";
        const errorCode = result.outcome === "REQUEST_FAILED" ? result.errorCode || undefined : undefined;
        return track("AuthorizeVerizonCustomerInAcss", { token }, result.outcome === "AUTHORIZED", {
            partner,
            outcome: result.outcome,
            ...(errorCode && { errorCode }),
        });
    };

    /**
     * A new verification (PIN or ACSS) starts over: drop any device and tokens from an earlier one, so neither a
     * lookup nor an activation can run on a line this attempt did not verify.
     */
    const startVerizonVerification = (token: string) => {
        setCustomerState({ activationFlowType: undefined });
        setVerizonReplacement(undefined);
        setVerifiedCustomerToken(token);
    };

    const lookUpVerizonReplacement = async () => {
        const payload = { ...defaultPayload, token: verifiedCustomerToken };
        const result = await prepareVerizonReplacement(ssoAccessToken, payload, logger, apiBaseUrl);
        const eligibilityCode = result.eligible ? undefined : result.code || result.errorCode || undefined;
        // An empty list falls back to the top-level device, as the lookup does, so it counts as one.
        const deviceCount = result.eligible ? result.devices?.length || 1 : undefined;
        return track("PrepareVerizonReplacement", result, result.eligible, { partner, eligibilityCode, deviceCount });
    };

    const selectClaim = (claimId: string) => {
        const claimSummary = customerState.claimSummaries.find((cl) => cl.claimId === claimId);
        if (!claimSummary) return "selectClaimFailure";

        setCustomerState({ activationFlowType: "REPLACEMENT_DEVICE", selectedClaimId: claimId });
        void dispatcher.dispatchBusinessEvent("SelectClaim", {
            activationFlowType: "REPLACEMENT_DEVICE",
            ...claimSummary,
            ...extraData,
        });

        // non-PA4 claims require extra OTP authorization before activation
        return claimSummary.requiresExtraAuth && !extraAuthorizedToken ? "generatePasscode" : "activateReady";
    };

    const submitActivation = async () => {
        const { activationFlowType } = customerState;
        // Verizon's device and line travel only inside the token from `prepareVerizonReplacement`; the
        // backend rejects device data from the browser, so none is sent.
        const payload = isVerizon
            ? { ...defaultPayload, activationFlowType }
            : {
                  ...defaultPayload,
                  activationFlowType,
                  claimId: customerState.selectedClaimId,
                  ...(customerState.imei
                      ? { manualDeviceInfo: { imei: customerState.imei, iccid: customerState.iccid } }
                      : undefined),
              };
        const {
            wasSuccessful,
            failureReason: reason,
            alreadyActive,
            failureCode,
            isRetryable,
        } = await activateDevice(ssoAccessToken, payload, activationToken, logger, apiBaseUrl);

        if (!wasSuccessful) pendingFailureReason.current = reason;

        // A surfaced reason means AT&T rejected on a business rule (Layer7) — retrying the same inputs will
        // fail the same way, so block retry until an activation answer changes; success clears it; transient
        // failures (no reason) stay retryable. Verizon says so explicitly: only NOT_RETRYABLE blocks, a
        // REJECTED reason is shown but the expert may retry. See `nextBlockedActivation`.
        setBlockedActivation((prev) =>
            nextBlockedActivation(
                prev,
                { wasSuccessful, failureReason: reason, isRetryable },
                activationInputSignature,
            ),
        );

        // reasonSurfaced: whether the expert was shown an actionable AT&T reason on the failure step.
        // Lets us measure feature coverage (what % of failures surface a reason) and segment the
        // un-retryable failures this targets from transient ones for the retry/time-saved before/after.
        // NOTE: the raw reason text is intentionally NOT sent here — some AT&T messages embed the
        // customer MDN (PII), and analytics flows to EIP/EDP. Per-reason breakdown comes from the
        // backend logs (`err.aggregateErrors`) instead.
        const reasonSurfaced = !wasSuccessful && reason !== undefined;
        if (reasonSurfaced && isRetryable !== true) {
            // Measures how often we prevent an un-retryable retry (pairs with the retry-count before/after).
            // PII-safe: no raw reason text (some AT&T reasons embed the customer MDN) — same rule as
            // reasonSurfaced above.
            void dispatcher.dispatchBusinessEvent("ActivateRetryBlocked", {
                activationFlowType,
                partner,
                ...(failureCode && { failureCode }),
                ...extraData,
            });
        }
        // `alreadyActive` and `failureCode` are Verizon's bounded outcome codes, never free text.
        void dispatcher.dispatchBusinessEvent("ActivateDevice", {
            activationFlowType,
            wasSuccessful,
            reasonSurfaced,
            partner,
            ...(isVerizon && {
                alreadyActive,
                failureCode,
                deviceSource: customerState.verizonReplacement?.typedImeiLast4 ? "TYPED" : "CLAIM",
            }),
            ...extraData,
        });
        return wasSuccessful;
    };

    const handlers: Record<
        ActivationAction,
        (input: ActivationActionInput) => ActivationActionResult | Promise<ActivationActionResult>
    > = {
        setCustomerState: ({ inputValue, option }) => {
            if (isInputCustomerField(option.value)) {
                setCustomerState({ [option.value]: inputValue });
                if (option.value === "phoneNumber") setRecoveryNotice(undefined);
            } else {
                logger.warn(
                    { ...extraData, field: option.value },
                    "Solve Guide | Device activation | setCustomerState for an unknown field, ignoring",
                );
            }
            return { status: "next", nextStep: "Continue" };
        },
        verifyPin: async ({ inputValue }) => {
            if (!hasValidCustomerMdn("verifyPin")) return { status: "recoveryNeeded" };
            const verification = resolveCustomerVerification(await verifyPin(inputValue));
            setCustomerState(verification.customerPatch);
            if (verification.token) setVerifiedCustomerToken(verification.token);
            return { status: "next", nextStep: verification.nextStep };
        },
        generatePasscodeForVerification: async ({ inputValue }) => {
            if (!hasValidCustomerMdn("generatePasscodeForVerification")) return { status: "recoveryNeeded" };
            const { success, transactionId } = await sendVerificationPasscode(inputValue);
            setCustomerState({ transactionId });
            return { status: "next", nextStep: success ? "success" : "failure" };
        },
        verifyPasscodeForVerification: async ({ inputValue }) => {
            if (!hasValidCustomerMdn("verifyPasscodeForVerification")) return { status: "recoveryNeeded" };
            const verification = resolveCustomerVerification(await verifyVerificationPasscode(inputValue));
            setCustomerState(verification.customerPatch);
            if (verification.token) setExtraAuthorizedToken(verification.token);
            return { status: "next", nextStep: verification.nextStep };
        },
        setActivationTypeToNew: () => {
            setCustomerState({ activationFlowType: "NEW_DEVICE" });
            return {
                status: "next",
                nextStep: extraAuthorizedToken ? undefined : "activateDifferentDeviceNeedsExtraAuth",
            };
        },
        generatePasscode: async ({ hasDropdown, selectedOption }) => {
            // Only demand a selection when the step actually offers one. Steps may fire this action from a
            // plain button to send the passcode to the account's own number, and an empty `mdn` is what the
            // pre-refactor code sent in that case. Unlike `selectClaim` (below), which is meaningless
            // without a claim, so it always requires the selection.
            if (hasDropdown && !selectedOption?.value) return { status: "needsDropdownSelection" };
            if (!hasVerificationToken("generatePasscode", verifiedCustomerToken)) return { status: "recoveryNeeded" };
            const { success, transactionId } = await sendPasscode(selectedOption?.value ?? "");
            setCustomerState({ transactionId });
            return { status: "next", nextStep: success ? "success" : "failure" };
        },
        verifyPasscode: async ({ inputValue }) => {
            if (!hasVerificationToken("verifyPasscode", verifiedCustomerToken)) return { status: "recoveryNeeded" };
            const { token } = await confirmPasscode(inputValue);
            if (token) setExtraAuthorizedToken(token);
            return { status: "next", nextStep: token ? "success" : "failure" };
        },
        verifyVerizonPin: async ({ inputValue }) => {
            // Flag off (or not Verizon): route to the authored failure step, which is the manual flow.
            if (!isVerizonApiEnabled) {
                logger.debug(
                    { ...extraData, partner },
                    "Solve Guide | Device activation | Verizon activation not enabled, using the manual flow",
                );
                return { status: "next", nextStep: "failure" };
            }
            if (!hasValidCustomerMdn("verifyVerizonPin")) return { status: "recoveryNeeded" };

            const { outcome, token } = await verifyVerizonPin(inputValue);
            startVerizonVerification(token);
            return { status: "next", nextStep: VERIZON_PIN_NEXT_STEP[outcome] };
        },
        authorizeVerizonInAcss: async () => {
            // Flag off (or not Verizon): route to the authored failure step, which is the manual flow.
            if (!isVerizonApiEnabled) {
                logger.debug(
                    { ...extraData, partner },
                    "Solve Guide | Device activation | Verizon activation not enabled, using the manual flow",
                );
                return { status: "next", nextStep: "failure" };
            }
            if (!hasValidCustomerMdn("authorizeVerizonInAcss")) return { status: "recoveryNeeded" };

            const { token } = await authorizeInAcss();
            startVerizonVerification(token);
            return { status: "next", nextStep: token ? "success" : "failure" };
        },
        prepareVerizonReplacement: async () => {
            // Flag off (or not Verizon): route to the authored failure step, which is the manual flow.
            if (!isVerizonApiEnabled) {
                logger.debug(
                    { ...extraData, partner },
                    "Solve Guide | Device activation | Verizon activation not enabled, using the manual flow",
                );
                return { status: "next", nextStep: "failure" };
            }
            if (!hasVerificationToken("prepareVerizonReplacement", verifiedCustomerToken)) {
                return { status: "recoveryNeeded" };
            }

            const result = await lookUpVerizonReplacement();
            if (result.eligible) {
                const { make, model, eSim, lineLast4, claimDate, token } = result;
                const options = result.devices?.length
                    ? result.devices
                    : [{ make, model, eSim, lineLast4, claimDate, token }];
                // The newest device until the expert picks one, so a guide with no device dropdown activates it.
                setVerizonReplacement(options[0], options);
                return { status: "next", nextStep: "success" };
            }

            // Drop devices and tokens from an earlier lookup so activation can never use them. The verification
            // stands, so the expert can go back and run the lookup again.
            setVerizonReplacement(undefined);
            return { status: "next", nextStep: result.code ? "noEligibleClaim" : "failure" };
        },
        selectVerizonReplacement: ({ selectedOption }) => {
            if (!selectedOption?.value) return { status: "needsDropdownSelection" };
            const index = Number(selectedOption.value);
            const selected = Number.isInteger(index) ? verizonReplacementOptions[index] : undefined;
            if (!selected) {
                logger.warn(
                    { ...extraData, deviceCount: verizonReplacementOptions.length, mountedWithStoredState },
                    "Solve Guide | Device activation | Selected Verizon device not found, skipping selection",
                );
                setRecoveryNotice(MISSING_VERIZON_REPLACEMENT_NOTICE);
                return { status: "recoveryNeeded" };
            }

            setVerizonReplacement(selected);
            // Make and model are catalog names, not customer data.
            track("SelectVerizonReplacement", undefined, true, {
                partner,
                deviceCount: verizonReplacementOptions.length,
                selectedIndex: index,
                make: selected.make,
                model: selected.model,
                eSim: selected.eSim,
            });
            return { status: "next", nextStep: "success" };
        },
        typeVerizonImei: () => {
            // The button is hidden without the flag; a flow that shows it anyway gets the manual flow.
            if (!isVerizonTypedImeiEnabled) {
                logger.debug(
                    { ...extraData, partner },
                    "Solve Guide | Device activation | Verizon typed IMEI not enabled, using the manual flow",
                );
                return { status: "next", nextStep: "failure" };
            }
            if (!hasVerificationToken("typeVerizonImei", verifiedCustomerToken)) return { status: "recoveryNeeded" };

            // Start clean: no looked-up device, IMEI or ICCID from earlier can be activated by mistake.
            setVerizonReplacement(undefined);
            setCustomerState({ imei: "", iccid: "" });
            track("TypeVerizonImei", undefined, true, { partner });
            return { status: "next", nextStep: "typeImei" };
        },
        submitTypedVerizonDevice: async ({ inputValue }) => {
            if (!isVerizonTypedImeiEnabled) {
                logger.debug(
                    { ...extraData, partner },
                    "Solve Guide | Device activation | Verizon typed IMEI not enabled, using the manual flow",
                );
                return { status: "next", nextStep: "failure" };
            }
            if (!hasVerificationToken("submitTypedVerizonDevice", verifiedCustomerToken)) {
                return { status: "recoveryNeeded" };
            }

            // The IMEI is the one the IMEI step stored. The ICCID is this step's input, so on a step with no text
            // input (the eSIM button) there is none, which means an eSIM.
            const imei = customerState.imei.replace(/\D/g, "");
            const iccid = inputValue.replace(/\D/g, "") || undefined;
            const eSim = !iccid;
            if (!VALID_IMEI.test(imei) || !hasValidImeiCheckDigit(imei)) {
                track("SubmitTypedVerizonDevice", undefined, false, { partner, eSim, outcome: "INVALID_IMEI" });
                return { status: "next", nextStep: "invalidImei" };
            }

            const payload = { ...defaultPayload, token: verifiedCustomerToken, imei, ...(iccid && { iccid }) };
            const result = await submitTypedVerizonDevice(ssoAccessToken, payload, logger, apiBaseUrl);
            // Never the IMEI, ICCID or token: only the SIM type and the outcome.
            track("SubmitTypedVerizonDevice", undefined, result.outcome === "ACCEPTED", {
                partner,
                eSim,
                outcome: result.outcome,
                ...(result.outcome === "REQUEST_FAILED" && result.errorCode && { errorCode: result.errorCode }),
            });
            if (result.outcome === "ACCEPTED") {
                setVerizonReplacement(result.device, [result.device]);
                return { status: "next", nextStep: "success" };
            }
            setVerizonReplacement(undefined);
            return { status: "next", nextStep: "failure" };
        },
        routeVerizonReplacementDevice: () => {
            // Never blocks the expert: with no stored device (e.g. state lost on a remount), or one we can't
            // match, `unknown` routes to the step where the expert picks the device type.
            const device = customerState.verizonReplacement;
            const route = routeVerizonPoa(device);
            // Make and model are catalog names, not customer data.
            track("RouteVerizonReplacementDevice", route, route !== "unknown", {
                partner,
                route,
                hasReplacementDevice: !!device,
                ...(device && { make: device.make, model: device.model, eSim: device.eSim }),
            });
            return { status: "next", nextStep: route };
        },
        selectClaim: ({ selectedOption }) => {
            if (!selectedOption?.value) return { status: "needsDropdownSelection" };
            return { status: "next", nextStep: selectClaim(selectedOption.value) };
        },
        activateDevice: async () => {
            if (!hasVerificationToken("activateDevice", activationToken)) return { status: "recoveryNeeded" };
            return { status: "next", nextStep: (await submitActivation()) ? "success" : "failure" };
        },
    };

    const deviceMake = typeof sessionMetadata.make === "string" ? sessionMetadata.make : undefined;
    const deviceModel = typeof sessionMetadata.model === "string" ? sessionMetadata.model : undefined;

    const dropdownOptions = useCallback(
        (authored: GuideOption[], dropdownAction?: string) => {
            // Flows may carry the dynamic customAction on one of the authored options (the button that
            // submits the selection) instead of on the dropdown's own action - AT&T's SelectClaim does.
            const dynamicAction =
                dropdownAction ??
                authored.find((option) => DYNAMIC_DROPDOWN_ACTIONS.has(option.customAction ?? ""))?.customAction;
            switch (dynamicAction) {
                case "generatePasscode":
                    return customerState.accountMdns.map((item) => ({ label: item.masked, value: item.value }));
                case "selectClaim":
                    return customerState.claimSummaries.map((item) => ({
                        label: `${item.deviceDescription} (${item.insuranceType})`,
                        value: item.claimId,
                    }));
                case "selectVerizonReplacement":
                    return verizonReplacementOptions.map(toVerizonReplacementOption);
                default:
                    return authored;
            }
        },
        [customerState.accountMdns, customerState.claimSummaries, verizonReplacementOptions],
    );

    const reset = useCallback(() => setBlockedActivation(undefined), []);

    const isOptionAvailable = useCallback(
        (option: GuideOption) => isVerizonTypedImeiEnabled || !TYPED_IMEI_ACTIONS.has(option.customAction ?? ""),
        [isVerizonTypedImeiEnabled],
    );

    return useMemo(
        () => ({
            assistContext: {
                activationFlowType: customerState.activationFlowType,
                make: deviceMake,
                model: deviceModel,
            },
            blockedReason: blockedActivation?.reason,
            deviceLabel: [deviceMake, deviceModel].filter(Boolean).join(" ").trim(),
            dropdownOptions,
            failureReason,
            handleAction: async (action: ActivationAction, input: ActivationActionInput) => handlers[action](input),
            isActivationBlocked: isRetryBlocked(blockedActivation, activationInputSignature),
            isActivationSubmitStep: actions.some((option) => option.customAction === "activateDevice"),
            isOptionAvailable,
            isVerizon,
            recoveryNotice,
            reset,
            shouldUseAlternateText:
                actions.some((option) => option.customAction === "activateDevice") &&
                normalizeMdn(customerState.phoneNumber) === normalizeMdn(taskMdn),
            verizonReplacement: customerState.verizonReplacement,
        }),
        [
            actions,
            activationInputSignature,
            blockedActivation,
            customerState,
            deviceMake,
            deviceModel,
            dropdownOptions,
            failureReason,
            handlers,
            isOptionAvailable,
            isVerizon,
            recoveryNotice,
            reset,
            taskMdn,
        ],
    );
}
